import type { QueryRowType, Transaction } from "@rocicorp/zero";
import type { ConnectedAppUsageType } from "../db/schema";
import { zql } from "./generatedDrizzleSchema";
import type { Permission } from "./rbac/permissions";
import {
  CROSS_RESOURCE_RULE_IDS,
  type CrossResourceAvailabilityRuleId,
  crossResourceRule,
  principalsWithPermission,
  requiredSupplierPermissions,
  type RbacReader,
  type ScopedGrantRow,
  userHasSupplierPermissionFromGrantReader,
  zqlGrantReader,
} from "./rbac/crossResourceAvailability";
import { grantHasExactPermission } from "./rbac/roles";

export const usageRef = {
  personalChat: (personalChatId: string) => ({
    type: "personal_chat" as const,
    refId: personalChatId,
    subKey: "",
  }),
  workflowSlug: (workflowId: string, slug: string) => ({
    type: "workflow_slug" as const,
    refId: workflowId,
    subKey: slug,
  }),
  commitStep: (commitId: string, stepDir: string) => ({
    type: "commit_step" as const,
    refId: commitId,
    subKey: stepDir,
  }),
  workflowChat: (workflowId: string) => ({
    type: "workflow_chat" as const,
    refId: workflowId,
    subKey: "",
  }),
  spaceGitRemote: (spaceId: string) => ({
    type: "space_git_remote" as const,
    refId: spaceId,
    subKey: "",
  }),
  skillGitSource: (sourceId: string) => ({
    type: "skill_git_source" as const,
    refId: sourceId,
    subKey: "",
  }),
  auditLogDestination: (destinationId: string) => ({
    type: "audit_log_destination" as const,
    refId: destinationId,
    subKey: "",
  }),
  aiProvider: (aiProviderId: string) => ({
    type: "ai_provider" as const,
    refId: aiProviderId,
    subKey: "",
  }),
};

type UsageRef = { type: ConnectedAppUsageType; refId: string; subKey: string };

export async function recordConnectorUsage(
  tx: Pick<Transaction, "mutate">,
  connectedAppId: string,
  ref: UsageRef,
  tenantId: string
) {
  await tx.mutate.connectedAppUsages.upsert({
    connectedAppId,
    type: ref.type,
    refId: ref.refId,
    subKey: ref.subKey,
    tenantId,
    createdAt: Date.now(),
  });
}

export async function dropConnectorUsage(
  tx: Pick<Transaction, "mutate">,
  connectedAppId: string,
  ref: UsageRef
) {
  await tx.mutate.connectedAppUsages.delete({
    connectedAppId,
    type: ref.type,
    refId: ref.refId,
    subKey: ref.subKey,
  });
}

// Commit-step usages with the branch + workflow needed to decide liveness,
// used by the public API to report usage at each branch's head commit.
export function commitStepUsageQuery(connectedAppIds: readonly string[]) {
  return zql.connectedAppUsages
    .where("type", "commit_step")
    .where("connectedAppId", "IN", connectedAppIds)
    .related("commit", (c) =>
      c.related("branch", (b) => b.related("workflow").one()).one()
    );
}

export type CommitStepUsageRow = QueryRowType<
  ReturnType<typeof commitStepUsageQuery>
>;

export type BranchHeadWorkflowUsage = {
  connectedAppId: string;
  workflowId: string;
  spaceId: string;
  stepDir: string;
  branchId: string;
  branchName: string;
  // Head of the main branch — the live, deployed version of the workflow.
  live: boolean;
};

// A usage at the head commit of any branch of a non-deleted workflow. Branches
// are a property of the space, not the viewer.
export function branchHeadWorkflowUsage(
  row: CommitStepUsageRow
): BranchHeadWorkflowUsage | null {
  const branch = row.commit?.branch;
  const workflow = branch?.workflow;
  if (!branch || branch.headCommitId !== row.refId) return null;
  if (!workflow || workflow.deletedAt != null) return null;
  return {
    connectedAppId: row.connectedAppId,
    workflowId: workflow.id,
    spaceId: workflow.spaceId,
    stepDir: row.subKey,
    branchId: branch.id,
    branchName: branch.name,
    live: branch.name === "main",
  };
}

type WriteZqlTx = Pick<Transaction, "run" | "mutate">;

function supplierPermissionsFor(
  ruleId: CrossResourceAvailabilityRuleId
): Permission[] {
  return [
    ...new Set(
      crossResourceRule(ruleId).tiers.flatMap(
        (tier) => tier.supplierPermissions
      )
    ),
  ];
}

async function dependentIdsUsingSupplier(
  grantReader: RbacReader,
  ruleId: CrossResourceAvailabilityRuleId,
  supplierId: string
): Promise<Set<string>> {
  const rule = crossResourceRule(ruleId);
  if (!rule.usageEdges) return new Set();
  const edges = await rule.usageEdges(grantReader, { supplierId });
  return new Set(edges.map((e) => e.dependentId));
}

// When a user leaves a group, the connector usages they can no longer reach
// are dropped rather than blocking the removal. `excludeGroupId` simulates the
// removal, so this is correct whether the membership grants are deleted before
// or after it runs.
export async function dropUsagesLostByGroupRemoval(
  tx: WriteZqlTx,
  tenantId: string,
  groupId: string,
  removedUserIds: readonly string[]
): Promise<void> {
  if (removedUserIds.length === 0) return;

  const grantReader = zqlGrantReader(tx, tenantId);
  const groupGrants = await grantReader.grantsForPrincipal({
    userGroupId: groupId,
  });

  for (const ruleId of CROSS_RESOURCE_RULE_IDS) {
    const rule = crossResourceRule(ruleId);
    if (rule.supplier.scope !== "connected_app") continue;
    if (rule.skipRemovalEnforcement) continue;

    const suppliersProviding = (permission: Permission) =>
      groupGrants
        .filter((g) => grantHasExactPermission(g, permission))
        .map((g) => g.scopeId);

    for (const userId of removedUserIds) {
      const userGrants = await grantReader.grantsForPrincipal({ userId });
      const otherGroupIds = new Set(
        userGrants.map((g) => g.scopeId).filter((id) => id !== groupId)
      );

      for (const permission of supplierPermissionsFor(ruleId)) {
        for (const supplierId of suppliersProviding(permission)) {
          if (
            await userHasSupplierPermissionFromGrantReader(
              grantReader,
              userId,
              supplierId,
              permission,
              groupId
            )
          )
            continue;

          if (rule.syntheticDependents)
            await dropUserPersonalChatUsages(tx, supplierId, userId);

          if (rule.usageEdges) {
            const dependentIds = await dependentIdsUsingSupplier(
              grantReader,
              ruleId,
              supplierId
            );
            if (dependentIds.size === 0) continue;
            const dependentGrants = await grantReader.grantsForScopes([
              ...dependentIds,
            ]);
            const reachedSpaceIds = new Set(
              dependentGrants
                .filter(
                  (g) =>
                    requiredSupplierPermissions(ruleId, g).includes(
                      permission
                    ) &&
                    (g.userId === userId ||
                      (g.userGroupId != null &&
                        otherGroupIds.has(g.userGroupId)))
                )
                .map((g) => g.scopeId)
            );
            for (const spaceId of reachedSpaceIds)
              await dropSpaceConnectorUsages(tx, supplierId, spaceId);
          }
        }
      }
    }
  }
}

// The supplier-side counterpart to dropUsagesLostByGroupRemoval: removing a
// principal's grants on a connector disconnects the dependents that relied on
// them (space workflows, git remotes, personal chats) instead of blocking the
// removal. Runs post-diff, so any route a principal keeps is respected.
export async function dropUsagesLostByGrantRemoval(
  tx: WriteZqlTx,
  tenantId: string,
  connectedAppId: string,
  removedGroupIds: readonly string[],
  removedUserIds: readonly string[],
  removedServiceAccountIds: readonly string[]
): Promise<void> {
  if (
    removedGroupIds.length === 0 &&
    removedUserIds.length === 0 &&
    removedServiceAccountIds.length === 0
  )
    return;

  const grantReader = zqlGrantReader(tx, tenantId);
  const remainingGrants = await grantReader.grantsForScope(connectedAppId);

  // Removed principals that actually lost `permission` — a principal that
  // keeps another grant with the same permission on the connector is unaffected.
  const principalsRemoved = (permission: Permission) => {
    const still = principalsWithPermission(remainingGrants, permission);
    return {
      groupIds: removedGroupIds.filter((id) => !still.groupIds.has(id)),
      userIds: removedUserIds.filter((id) => !still.userIds.has(id)),
      serviceAccountIds: removedServiceAccountIds.filter(
        (id) => !still.serviceAccountIds.has(id)
      ),
    };
  };

  // Users who lost `permission`: removed directly or via a removed group's
  // membership, and keeping it through no other grant post-diff.
  const lostUsersByPermission = new Map<Permission, Set<string>>();
  const lostUsers = async (
    permission: Permission,
    removed: { groupIds: string[]; userIds: string[] }
  ): Promise<Set<string>> => {
    const cached = lostUsersByPermission.get(permission);
    if (cached) return cached;
    const candidates = new Set(removed.userIds);
    if (removed.groupIds.length > 0) {
      const memberGrants = await grantReader.grantsForScopes(removed.groupIds);
      for (const g of memberGrants) if (g.userId) candidates.add(g.userId);
    }
    const lost = new Set<string>();
    for (const userId of candidates) {
      if (
        !(await userHasSupplierPermissionFromGrantReader(
          grantReader,
          userId,
          connectedAppId,
          permission
        ))
      )
        lost.add(userId);
    }
    lostUsersByPermission.set(permission, lost);
    return lost;
  };

  for (const ruleId of CROSS_RESOURCE_RULE_IDS) {
    const rule = crossResourceRule(ruleId);
    if (rule.supplier.scope !== "connected_app") continue;
    if (rule.skipRemovalEnforcement) continue;

    let dependentGrants: ScopedGrantRow[] = [];
    if (rule.usageEdges) {
      const dependentIds = await dependentIdsUsingSupplier(
        grantReader,
        ruleId,
        connectedAppId
      );
      if (dependentIds.size > 0)
        dependentGrants = await grantReader.grantsForScopes([...dependentIds]);
    }
    const syntheticDependents = rule.syntheticDependents
      ? await rule.syntheticDependents(grantReader, connectedAppId)
      : [];

    const brokenSpaceIds = new Set<string>();
    const brokenChatUserIds = new Set<string>();

    for (const permission of supplierPermissionsFor(ruleId)) {
      const removed = principalsRemoved(permission);
      if (
        removed.groupIds.length === 0 &&
        removed.userIds.length === 0 &&
        removed.serviceAccountIds.length === 0
      )
        continue;

      const removedGroups = new Set(removed.groupIds);
      const removedServiceAccounts = new Set(removed.serviceAccountIds);
      const lost = await lostUsers(permission, removed);

      for (const g of dependentGrants) {
        if (!requiredSupplierPermissions(ruleId, g).includes(permission))
          continue;
        // A group's or service account's grant must be covered by that
        // principal directly (a service account holds only its own grants,
        // never a group's); a user's grant accepts any of their remaining
        // routes.
        if (g.userGroupId != null && removedGroups.has(g.userGroupId))
          brokenSpaceIds.add(g.scopeId);
        else if (
          g.serviceAccountId != null &&
          removedServiceAccounts.has(g.serviceAccountId)
        )
          brokenSpaceIds.add(g.scopeId);
        else if (g.userId != null && lost.has(g.userId))
          brokenSpaceIds.add(g.scopeId);
      }

      for (const d of syntheticDependents) {
        if (d.permission === permission && lost.has(d.userId))
          brokenChatUserIds.add(d.userId);
      }
    }

    for (const spaceId of brokenSpaceIds)
      await dropSpaceConnectorUsages(tx, connectedAppId, spaceId);
    for (const userId of brokenChatUserIds)
      await dropUserPersonalChatUsages(tx, connectedAppId, userId);
  }
}

async function dropUserPersonalChatUsages(
  tx: WriteZqlTx,
  connectedAppId: string,
  userId: string
): Promise<void> {
  const rows = await tx.run(
    zql.connectedAppUsages
      .where("type", "personal_chat")
      .where("connectedAppId", connectedAppId)
      .related("personalChat")
  );
  for (const row of rows) {
    if (row.personalChat?.userId !== userId) continue;
    await dropConnectorUsage(tx, connectedAppId, {
      type: row.type,
      refId: row.refId,
      subKey: row.subKey,
    });
  }
}

// Disconnects a single workflow from a connector: its slug mapping plus the
// commit_step rows of its commits. Steps keep their config entry and render as
// missing until a replacement is associated.
export async function dropWorkflowConnectorUsages(
  tx: WriteZqlTx,
  connectedAppId: string,
  workflowId: string
): Promise<void> {
  const slugRows = await tx.run(
    zql.connectedAppUsages
      .where("type", "workflow_slug")
      .where("connectedAppId", connectedAppId)
      .where("refId", workflowId)
  );
  for (const row of slugRows) {
    await dropConnectorUsage(tx, connectedAppId, {
      type: row.type,
      refId: row.refId,
      subKey: row.subKey,
    });
  }

  const commitSteps = await tx.run(
    zql.connectedAppUsages
      .where("type", "commit_step")
      .where("connectedAppId", connectedAppId)
      .related("commit", (c) => c.related("branch").one())
  );
  for (const row of commitSteps) {
    if (row.commit?.branch?.workflowId !== workflowId) continue;
    await dropConnectorUsage(tx, connectedAppId, {
      type: row.type,
      refId: row.refId,
      subKey: row.subKey,
    });
  }
}

async function dropSpaceConnectorUsages(
  tx: WriteZqlTx,
  connectedAppId: string,
  spaceId: string
): Promise<void> {
  const workflowUsages = await tx.run(
    zql.connectedAppUsages
      .where("type", "IN", ["workflow_slug", "workflow_chat"])
      .where("connectedAppId", connectedAppId)
      .related("workflow")
  );
  for (const row of workflowUsages) {
    if (row.workflow?.spaceId !== spaceId) continue;
    await dropConnectorUsage(tx, connectedAppId, {
      type: row.type,
      refId: row.refId,
      subKey: row.subKey,
    });
  }

  const commitSteps = await tx.run(
    zql.connectedAppUsages
      .where("type", "commit_step")
      .where("connectedAppId", connectedAppId)
      .related("commit", (c) =>
        c.related("branch", (b) => b.related("workflow").one()).one()
      )
  );
  for (const row of commitSteps) {
    if (row.commit?.branch?.workflow?.spaceId !== spaceId) continue;
    await dropConnectorUsage(tx, connectedAppId, {
      type: row.type,
      refId: row.refId,
      subKey: row.subKey,
    });
  }

  const remotes = await tx.run(
    zql.connectedAppUsages
      .where("type", "space_git_remote")
      .where("connectedAppId", connectedAppId)
      .where("refId", spaceId)
  );
  for (const row of remotes) {
    await dropConnectorUsage(tx, connectedAppId, {
      type: row.type,
      refId: row.refId,
      subKey: row.subKey,
    });
  }
}
